import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
} from "@angular/core";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

import { DataService } from "../../providers/data/data.service";
import { NotificationService } from "../../providers/notification/notification.service";
import { PreorderBrandReportService } from "../../providers/preorder-brand-report/preorder-brand-report.service";
import { GET_PUBLISHED_PRICELISTS } from "../pricelists-page/pricelists-page.graphql";
import { SUPPLY_TYPE } from "../../../shared/constants/pricelist.constants";
import { GetPublishedPricelistsResponse } from "../../../shared/types/pricelist.types";
import { GET_CATALOG_BRANDS } from "./brands-page.graphql";
import {
    ALPHABET,
    BRANDS_FETCH_TAKE,
    BRANDS_PAGE_TEXT,
    slugifyBrandName,
} from "./brands-page.constants";

export interface BrandItem {
    id: string;
    name: string;
    letter: string;
}

export interface BrandLetterGroup {
    letter: string;
    brands: BrandItem[];
}

interface GetCatalogBrandsResponse {
    catalogBrands: {
        totalItems: number;
        items: Array<{
            id: string;
            name: string;
            code?: string;
        }>;
    };
}

@Component({
    selector: "vsf-brands-page",
    templateUrl: "./brands-page.component.html",
    styleUrls: ["./brands-page.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BrandsPageComponent implements OnInit, OnDestroy {
    readonly TEXT = BRANDS_PAGE_TEXT;
    readonly alphabet = ALPHABET;

    brands: BrandItem[] = [];
    letterGroups: BrandLetterGroup[] = [];
    availableLetters = new Set<string>();
    selectedLetter: string | null = null;
    loading = true;
    downloadingBrandReport = false;
    readonly skeletonColumns = [
        [[0, 1, 2, 3, 4, 5], [0, 1, 2, 3, 4]],
        [[0, 1, 2, 3, 4, 5, 6], [0, 1, 2, 3]],
    ];

    private destroy$ = new Subject<void>();

    constructor(
        private dataService: DataService,
        private notificationService: NotificationService,
        private preorderBrandReportService: PreorderBrandReportService,
        private cd: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        this.loadBrands();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    get filteredGroups(): BrandLetterGroup[] {
        if (!this.selectedLetter) {
            return this.letterGroups;
        }
        return this.letterGroups.filter((g) => g.letter === this.selectedLetter);
    }

    selectLetter = (letter: string | null): void => {
        this.selectedLetter = letter;
        this.cd.markForCheck();
    };

    isLetterAvailable = (letter: string): boolean =>
        this.availableLetters.has(letter);

    brandSlug = (name: string): string => slugifyBrandName(name);

    trackByLetter = (_index: number, group: BrandLetterGroup): string =>
        group.letter;

    trackByBrand = (_index: number, brand: BrandItem): string => brand.id;

    downloadPreorderBrandlists = (): void => {
        if (this.downloadingBrandReport) {
            return;
        }
        this.downloadingBrandReport = true;
        this.cd.markForCheck();

        this.dataService
            .query<GetPublishedPricelistsResponse>(
                GET_PUBLISHED_PRICELISTS,
                { supplyType: SUPPLY_TYPE.PRE_ORDER },
                "network-only",
            )
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (result) => {
                    this.preorderBrandReportService
                        .downloadPdfReport(result.publishedPricelists ?? [])
                        .catch((error) => {
                            const message =
                                error instanceof Error
                                    ? error.message
                                    : String(error);
                            this.notificationService
                                .error("Error: " + message)
                                .subscribe();
                        })
                        .finally(() => {
                            this.downloadingBrandReport = false;
                            this.cd.markForCheck();
                        });
                },
                error: (error) => {
                    console.error("Error loading pre-order brandlists:", error);
                    this.notificationService
                        .error(
                            "Error: " +
                                (error?.message ||
                                    "Could not load pre-order brandlists"),
                        )
                        .subscribe();
                    this.downloadingBrandReport = false;
                    this.cd.markForCheck();
                },
            });
    };

    private loadBrands = (): void => {
        this.loading = true;
        this.cd.markForCheck();

        this.dataService
            .query<GetCatalogBrandsResponse>(
                GET_CATALOG_BRANDS,
                {
                    take: BRANDS_FETCH_TAKE,
                    skip: 0,
                },
                "network-only",
            )
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (result) => {
                    const items = result.catalogBrands?.items ?? [];
                    const seen = new Set<string>();
                    this.brands = items
                        .map((item) => ({
                            id: String(item.id),
                            name: item.name,
                            letter: this.getLetter(item.name),
                        }))
                        .filter((brand) => {
                            if (seen.has(brand.id)) {
                                return false;
                            }
                            seen.add(brand.id);
                            return Boolean(brand.name?.trim());
                        })
                        .sort((a, b) =>
                            a.name.localeCompare(b.name, undefined, {
                                sensitivity: "base",
                            }),
                        );
                    this.buildLetterGroups();
                    this.loading = false;
                    this.cd.markForCheck();
                },
                error: (error) => {
                    console.error("Error loading brands:", error);
                    this.brands = [];
                    this.letterGroups = [];
                    this.availableLetters = new Set();
                    this.loading = false;
                    this.cd.markForCheck();
                },
            });
    };

    private getLetter = (name: string): string => {
        const first = name.trim().charAt(0).toUpperCase();
        return /[A-Z]/.test(first) ? first : BRANDS_PAGE_TEXT.OTHER;
    };

    private buildLetterGroups = (): void => {
        const map = new Map<string, BrandItem[]>();
        for (const brand of this.brands) {
            const list = map.get(brand.letter) ?? [];
            list.push(brand);
            map.set(brand.letter, list);
        }

        const letters = [
            ...ALPHABET.filter((l) => map.has(l)),
            ...(map.has(BRANDS_PAGE_TEXT.OTHER)
                ? [BRANDS_PAGE_TEXT.OTHER]
                : []),
        ];

        this.availableLetters = new Set(letters);
        this.letterGroups = letters.map((letter) => ({
            letter,
            brands: map.get(letter) ?? [],
        }));
    };
}
